<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { useStorage } from '@vueuse/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import type { ConnectionConfig, HopConfig } from '@/composables/use-ssh';

const emit = defineEmits<{
	connect: [config: ConnectionConfig];
}>();

const props = defineProps<{
	loading?: boolean;
	error?: string | null;
}>();

const defaultGatewayUrl = window.location.origin.replace(/^http/, 'ws');

type HopEntry = {
	hostname: string;
	port: string;
	user: string;
	password: string;
	privateKeyText: string;
	privateKeyFileName: string;
	passphrase: string;
};

type FormState = {
	gatewayUrl: string;
	hostname: string;
	port: string;
	user: string;
	password: string;
	privateKeyText: string;
	privateKeyFileName: string;
	passphrase: string;
	hops: HopEntry[];
};

const form = useStorage<FormState>('ssh-form', {
	gatewayUrl: defaultGatewayUrl,
	hostname: '',
	port: '22',
	user: '',
	password: '',
	privateKeyText: '',
	privateKeyFileName: '',
	passphrase: '',
	hops: [],
});

const activeTab = ref('destination');

const hopFileInputs = ref<HTMLInputElement[]>([]);

function addHop() {
	// demote current destination to a hop, then clear destination fields
	form.value.hops.push({
		hostname: form.value.hostname,
		port: form.value.port,
		user: form.value.user,
		password: form.value.password,
		privateKeyText: form.value.privateKeyText,
		privateKeyFileName: form.value.privateKeyFileName,
		passphrase: form.value.passphrase,
	});
	form.value.hostname = '';
	form.value.port = '22';
	form.value.user = '';
	form.value.password = '';
	form.value.privateKeyText = '';
	form.value.privateKeyFileName = '';
	form.value.passphrase = '';
	nextTick(() => {
		activeTab.value = 'destination';
	});
}

function removeHop(index: number) {
	form.value.hops.splice(index, 1);
	// switch to destination or the previous hop
	if (form.value.hops.length === 0) {
		activeTab.value = 'destination';
	} else if (index >= form.value.hops.length) {
		activeTab.value = `hop-${form.value.hops.length - 1}`;
	} else {
		activeTab.value = `hop-${index}`;
	}
}

function removeDestination() {
	// pop the last hop and promote it to destination
	const lastHop = form.value.hops.pop();
	if (lastHop) {
		form.value.hostname = lastHop.hostname;
		form.value.port = lastHop.port;
		form.value.user = lastHop.user;
		form.value.password = lastHop.password;
		form.value.privateKeyText = lastHop.privateKeyText;
		form.value.privateKeyFileName = lastHop.privateKeyFileName;
		form.value.passphrase = lastHop.passphrase;
	} else {
		// no hops left — just clear destination fields
		form.value.hostname = '';
		form.value.port = '22';
		form.value.user = '';
		form.value.password = '';
		form.value.privateKeyText = '';
		form.value.privateKeyFileName = '';
		form.value.passphrase = '';
	}
	activeTab.value = 'destination';
}

function onHopFileSelect(event: Event, hop: HopEntry) {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];
	if (!file) return;

	hop.privateKeyFileName = file.name;
	const reader = new FileReader();
	reader.onload = () => {
		hop.privateKeyText = reader.result as string;
	};
	reader.readAsText(file);
}

function clearHopKey(hop: HopEntry) {
	hop.privateKeyText = '';
	hop.privateKeyFileName = '';
	hop.passphrase = '';
}

function onFileSelect(event: Event) {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];
	if (!file) return;

	form.value.privateKeyFileName = file.name;
	const reader = new FileReader();
	reader.onload = () => {
		form.value.privateKeyText = reader.result as string;
	};
	reader.readAsText(file);
}

function clearPrivateKey() {
	form.value.privateKeyText = '';
	form.value.privateKeyFileName = '';
	form.value.passphrase = '';
}

function buildHops(): HopConfig[] {
	return form.value.hops
		.filter((h) => h.hostname && h.user)
		.map((h) => ({
			hostname: h.hostname,
			port: parseInt(h.port, 10) || 22,
			user: h.user,
			password: h.password || undefined,
			privateKey: h.privateKeyText || undefined,
			passphrase: h.passphrase || undefined,
		}));
}

function onSubmit() {
	if (!form.value.hostname || !form.value.user) return;

	emit('connect', {
		gatewayUrl: form.value.gatewayUrl,
		hostname: form.value.hostname,
		port: parseInt(form.value.port, 10) || 22,
		user: form.value.user,
		password: form.value.password || undefined,
		privateKey: form.value.privateKeyText || undefined,
		passphrase: form.value.passphrase || undefined,
		hops: buildHops(),
	});
}

// label for the destination tab showing host or fallback
const destLabel = computed(() => {
	if (form.value.hostname) return form.value.hostname;
	return 'Destination';
});
</script>

<template>
	<Card class="w-full max-w-md">
		<CardHeader>
			<CardTitle>SSH Connection</CardTitle>
			<CardDescription>Connect to a remote server through the WebSocket gateway.</CardDescription>
		</CardHeader>
		<CardContent>
			<form @submit.prevent="onSubmit" class="space-y-4">
				<div class="space-y-2">
					<Label for="gateway">Gateway URL</Label>
					<Input id="gateway" v-model="form.gatewayUrl" placeholder="ws://localhost:5173" />
				</div>

				<Tabs v-model="activeTab">
					<div class="flex items-center gap-1">
						<TabsList class="flex-1 flex-wrap h-auto gap-1 justify-start">
							<TabsTrigger
								v-for="(hop, index) in form.hops"
								:key="'hop-' + index"
								:value="'hop-' + index"
								class="text-xs px-2 py-1 gap-1"
							>
								{{ hop.hostname || `Hop ${index + 1}` }}
								<span
									class="ml-0.5 rounded-sm opacity-60 hover:opacity-100 hover:bg-muted-foreground/20 inline-flex items-center justify-center h-4 w-4"
									@click.stop="removeHop(index)"
									>&times;</span
								>
							</TabsTrigger>
							<TabsTrigger value="destination" class="text-xs px-2 py-1 gap-1">
								{{ destLabel }}
								<span
									class="ml-0.5 rounded-sm opacity-60 hover:opacity-100 hover:bg-muted-foreground/20 inline-flex items-center justify-center h-4 w-4"
									@click.stop="removeDestination()"
									>&times;</span
								>
							</TabsTrigger>
						</TabsList>
						<Button
							type="button"
							variant="outline"
							size="sm"
							class="h-7 w-7 p-0 shrink-0"
							@click="addHop"
							title="Add jump host"
						>
							+
						</Button>
					</div>

					<!-- hop tabs -->
					<TabsContent
						v-for="(hop, index) in form.hops"
						:key="'hop-content-' + index"
						:value="'hop-' + index"
						class="space-y-3"
					>
						<div class="grid grid-cols-3 gap-3">
							<div class="col-span-2 space-y-2">
								<Label :for="'hop-host-' + index">Hostname</Label>
								<Input
									:id="'hop-host-' + index"
									v-model="hop.hostname"
									placeholder="bastion.example.com"
								/>
							</div>
							<div class="space-y-2">
								<Label :for="'hop-port-' + index">Port</Label>
								<Input :id="'hop-port-' + index" v-model="hop.port" type="number" />
							</div>
						</div>

						<div class="space-y-2">
							<Label :for="'hop-user-' + index">Username</Label>
							<Input :id="'hop-user-' + index" v-model="hop.user" placeholder="root" />
						</div>

						<div class="space-y-2">
							<Label :for="'hop-pass-' + index">Password</Label>
							<Input
								:id="'hop-pass-' + index"
								v-model="hop.password"
								type="password"
								placeholder="optional if using key"
							/>
						</div>

						<div class="space-y-2">
							<Label>Private Key</Label>
							<div class="flex items-center gap-2">
								<Button
									type="button"
									variant="outline"
									size="sm"
									class="shrink-0"
									@click="hopFileInputs[index]?.click()"
								>
									{{ hop.privateKeyFileName || 'Select key file' }}
								</Button>
								<Button
									v-if="hop.privateKeyFileName"
									type="button"
									variant="ghost"
									size="sm"
									@click="clearHopKey(hop)"
								>
									Clear
								</Button>
								<input
									ref="hopFileInputs"
									type="file"
									class="hidden"
									accept=".pem,.key,id_*"
									@change="onHopFileSelect($event, hop)"
								/>
							</div>
						</div>

						<div v-if="hop.privateKeyFileName" class="space-y-2">
							<Label :for="'hop-phrase-' + index">Key Passphrase</Label>
							<Input
								:id="'hop-phrase-' + index"
								v-model="hop.passphrase"
								type="password"
								placeholder="leave blank if unencrypted"
							/>
						</div>
					</TabsContent>

					<!-- destination tab -->
					<TabsContent value="destination" class="space-y-3">
						<div class="grid grid-cols-3 gap-3">
							<div class="col-span-2 space-y-2">
								<Label for="hostname">Hostname</Label>
								<Input id="hostname" v-model="form.hostname" placeholder="192.168.1.100" />
							</div>
							<div class="space-y-2">
								<Label for="port">Port</Label>
								<Input id="port" v-model="form.port" type="number" />
							</div>
						</div>

						<div class="space-y-2">
							<Label for="user">Username</Label>
							<Input id="user" v-model="form.user" placeholder="root" />
						</div>

						<div class="space-y-2">
							<Label for="password">Password</Label>
							<Input
								id="password"
								v-model="form.password"
								type="password"
								placeholder="optional if using key"
							/>
						</div>

						<div class="space-y-2">
							<Label>Private Key</Label>
							<div class="flex items-center gap-2">
								<Button
									type="button"
									variant="outline"
									size="sm"
									class="shrink-0"
									@click="($refs.fileInput as HTMLInputElement).click()"
								>
									{{ form.privateKeyFileName || 'Select key file' }}
								</Button>
								<Button
									v-if="form.privateKeyFileName"
									type="button"
									variant="ghost"
									size="sm"
									@click="clearPrivateKey"
								>
									Clear
								</Button>
								<input
									ref="fileInput"
									type="file"
									class="hidden"
									accept=".pem,.key,id_*"
									@change="onFileSelect"
								/>
							</div>
						</div>

						<div v-if="form.privateKeyFileName" class="space-y-2">
							<Label for="passphrase">Key Passphrase</Label>
							<Input
								id="passphrase"
								v-model="form.passphrase"
								type="password"
								placeholder="leave blank if unencrypted"
							/>
						</div>
					</TabsContent>
				</Tabs>

				<div
					v-if="props.error"
					class="rounded-md bg-destructive/10 p-3 text-sm text-destructive-foreground"
				>
					{{ props.error }}
				</div>

				<Button
					type="submit"
					class="w-full"
					:disabled="!form.hostname || !form.user || props.loading"
				>
					{{ props.loading ? 'Connecting...' : 'Connect' }}
				</Button>
			</form>
		</CardContent>
	</Card>
</template>
